/**
 * @file        src/modals/WorldMapModal.jsx
 * @description Full-screen "world map" overlay with stylised continent
 *              paths and pin clusters (cities + counts). Ported from
 *              `#worldMapModal` (~line 1942) in the design HTML.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-06-22 Claude — Initial port. Pins are rendered from `WM_PINS`
 *                         (../data/worldMap.js). The "Filter chips" row
 *                         and the +/- zoom controls are visual only — the
 *                         design ships them as static UI.
 */
import { useEffect } from 'react';
import { WM_PINS } from '../data/worldMap.js';
import BrandLogo, { LOGO_SIZE } from '../components/BrandLogo.jsx';

function Pin({ x, y, n }) {
  if (!n) {
    return (
      <span
        className="absolute w-2.5 h-2.5 rounded-full bg-ink/70 border border-night -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${x}%`, top: `${y}%` }}
      />
    );
  }
  const numeric = !isNaN(+n);
  if (!numeric) {
    return (
      <>
        <span
          className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 -translate-y-[140%]"
          style={{ left: `${x}%`, top: `${y}%` }}
        >
          <span className="px-1.5 py-0.5 rounded bg-night/80 border border-line text-[10px] text-ink whitespace-nowrap">{n}</span>
        </span>
        <span
          className="absolute w-2.5 h-2.5 rounded-full bg-neon border border-night -translate-x-1/2 -translate-y-1/2 shadow-lg"
          style={{ left: `${x}%`, top: `${y}%` }}
        />
      </>
    );
  }
  const big = +n > 30;
  return (
    <span
      className={`absolute -translate-x-1/2 -translate-y-1/2 min-w-6 h-6 px-1.5 rounded-full ${big ? 'bg-sky' : 'bg-neon'} text-onaccent text-[11px] font-bold flex items-center justify-center shadow-lg ring-2 ring-ink/20 cursor-pointer hover:scale-110 transition`}
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      {n}
    </span>
  );
}

export default function WorldMapModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center">
      <div onClick={onClose} className="absolute inset-0 bg-night/85 backdrop-blur-sm" />
      <div className="relative w-full h-full md:w-[96vw] md:h-[94svh] md:rounded-3xl overflow-hidden border border-line bg-[#0b1322]">
        {/* top bar */}
        <div className="absolute top-3 left-3 right-3 z-20 flex items-start justify-between gap-3 pointer-events-none">
          <div className="flex flex-wrap items-center gap-2 pointer-events-auto">
            <div className="flex items-center gap-2 bg-panel/95 border border-line rounded-xl px-3 h-10 w-[230px] sm:w-[280px]">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-muted shrink-0"><path fill="currentColor" d="m21 20-5.2-5.2a7 7 0 1 0-1.4 1.4L20 21zM5 10a5 5 0 1 1 10 0 5 5 0 0 1-10 0" /></svg>
              <input placeholder="Search anywhere in the world" className="bg-transparent outline-none text-sm w-full placeholder:text-muted" />
            </div>
            <div className="hidden lg:flex flex-wrap gap-1.5" />
          </div>
          <div className="flex items-center gap-3 pointer-events-auto">
            <BrandLogo className={'hidden sm:block ' + LOGO_SIZE} full colorClassName="text-ink" />
            <button className="flex items-center gap-2 bg-panel/95 border border-line rounded-xl px-3 h-10 text-sm hover:border-neon cursor-pointer">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-neon"><path fill="currentColor" d="M3 5h18v2H3zm0 6h12v2H3zm0 6h18v2H3z" /></svg>
              My Itineraries
            </button>
            <button onClick={onClose} className="w-10 h-10 rounded-xl bg-panel/95 border border-line flex items-center justify-center hover:border-neon cursor-pointer">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
            </button>
          </div>
        </div>
        {/* world map */}
        <svg viewBox="0 0 1000 520" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full">
          <rect width="1000" height="520" fill="#0c1a2b" />
          <g fill="#14302a" stroke="#1c4a3c" strokeWidth="1.5">
            <path d="M70 70 Q150 50 240 80 Q300 100 290 150 Q330 170 300 220 Q260 260 210 240 Q170 270 150 230 Q110 250 95 200 Q60 180 80 130 Q55 100 70 70Z" />
            <path d="M250 300 Q300 290 305 340 Q325 380 290 430 Q270 480 245 450 Q225 400 235 360 Q230 320 250 300Z" />
            <path d="M360 50 Q410 40 420 80 Q400 120 360 110 Q345 80 360 50Z" />
            <path d="M470 90 Q540 75 560 110 Q540 150 500 150 Q470 140 470 90Z" />
            <path d="M480 200 Q560 185 575 240 Q580 300 540 360 Q510 400 490 350 Q460 300 470 250 Q465 215 480 200Z" />
            <path d="M580 80 Q760 55 880 110 Q920 150 850 190 Q780 200 720 175 Q650 200 600 160 Q560 130 580 80Z" />
            <path d="M650 200 Q690 195 700 235 Q685 275 660 255 Q635 225 650 200Z" />
            <path d="M760 250 Q840 240 880 275 Q840 300 780 290 Q745 275 760 250Z" />
            <path d="M800 350 Q890 335 910 385 Q885 430 820 420 Q775 395 800 350Z" />
          </g>
          <g stroke="#16324a" strokeWidth="1" opacity="0.5">
            <path d="M0 130H1000M0 260H1000M0 390H1000" fill="none" />
            <path d="M200 0V520M400 0V520M600 0V520M800 0V520" fill="none" />
          </g>
        </svg>
        <div className="absolute inset-0">
          {(WM_PINS || []).map(([x, y, n], i) => (
            <Pin key={i} x={x} y={y} n={n} />
          ))}
        </div>
        <div className="absolute bottom-3 left-4 text-[10px] text-muted/70">Beyond Destination Maps · stylised</div>
        <div className="absolute bottom-5 right-5 flex flex-col gap-2 z-20">
          <button className="w-10 h-10 rounded-lg bg-panel/95 border border-line flex items-center justify-center text-muted hover:text-neon cursor-pointer">
            <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M12 2 2 7l10 5 10-5zm0 8L2 15l10 5 10-5z" /></svg>
          </button>
          <button className="w-10 h-10 rounded-lg bg-panel/95 border border-line flex items-center justify-center text-muted hover:text-neon cursor-pointer">
            <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8m9 3h-2.1a7 7 0 0 0-5.9-5.9V3h-2v2.1A7 7 0 0 0 5.1 11H3v2h2.1a7 7 0 0 0 5.9 5.9V21h2v-2.1a7 7 0 0 0 5.9-5.9H21z" /></svg>
          </button>
          <div className="rounded-lg bg-panel/95 border border-line overflow-hidden">
            <button className="w-10 h-9 flex items-center justify-center text-muted hover:text-neon cursor-pointer border-b border-line text-lg">+</button>
            <button className="w-10 h-9 flex items-center justify-center text-muted hover:text-neon cursor-pointer text-lg">−</button>
          </div>
        </div>
      </div>
    </div>
  );
}
